/**
 * Summary of the active planning (name, edit lock, revision) for the shell.
 * Prefers the live copy in the planning store (kept current over SSE) and
 * otherwise takes it from the planning list (GET /api/plannings, a cheap
 * catalog query): the ProjectLibre lock banner must not wait until the server
 * has loaded the whole planning into memory, as GET /api/plannings/:id does.
 */
import { useQuery } from '@tanstack/react-query';
import type { PlanningSummaryDto } from '../../shared/api.js';
import { api } from '../lib/api.js';
import { qk } from '../lib/queries.js';
import { planningIdOf, useMe } from '../lib/session.js';
import { usePlanningStore } from '../store/planningStore.js';

export function usePlanningSummary(): PlanningSummaryDto | null {
  const planningId = planningIdOf(useMe());
  const storeId = usePlanningStore((s) => s.planningId);
  const storePlanning = usePlanningStore((s) => s.planning);
  const live = planningId !== null && storeId === planningId && storePlanning ? storePlanning : null;
  const list = useQuery({
    queryKey: qk.plannings,
    queryFn: ({ signal }) => api.get<PlanningSummaryDto[]>('/api/plannings', { signal }),
    enabled: planningId !== null && live === null,
    staleTime: 60_000,
  });
  return live ?? list.data?.find((p) => p.id === planningId) ?? null;
}
